import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Clock, MapPin, Power, PowerOff } from "lucide-react";

const STATUS_META = {
  REQUESTED: "bg-sky-100 text-sky-900",
  ASSIGNED: "bg-blue-100 text-blue-900",
  ACCEPTED: "bg-blue-100 text-blue-900",
  DECLINED_BY_TECH: "bg-amber-100 text-amber-900",
  ON_VISIT: "bg-indigo-100 text-indigo-900",
  ESTIMATED: "bg-amber-100 text-amber-900",
  APPROVED_BY_CUSTOMER: "bg-indigo-100 text-indigo-900",
  REJECTED_BY_CUSTOMER: "bg-rose-100 text-rose-900",
  COMPLETED: "bg-emerald-100 text-emerald-900",
  CANCELLED: "bg-slate-100 text-slate-700",
  DISPUTED: "bg-rose-200 text-rose-900",
};

function Status({ s }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_META[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
}

export default function AdminPilotBookings() {
  const [bookings, setBookings] = useState(null);
  const [filter, setFilter] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [chosen, setChosen] = useState("");
  const [current, setCurrent] = useState(null);
  const [events, setEvents] = useState([]);
  const [fixtureInfo, setFixtureInfo] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [resolvingDispute, setResolvingDispute] = useState(false);
  const [resolution, setResolution] = useState({
    outcome: "CANCELLED",
    ca_opinion_reference: "",
    reason: "",
  });

  const refreshAll = async () => {
    try {
      const [bk, pil, vd, fixture] = await Promise.all([
        api.get("/admin/pilot/bookings"),
        api.get("/admin/pilots"),
        api.get("/admin/vendors").catch(() => ({ data: { vendors: [] } })),
        api.get("/admin/pilot/bookings/fixtures/state").catch(() => ({ data: { active: false } })),
      ]);
      setBookings(bk.data.bookings || []);
      setPilots(pil.data.pilots || []);
      const list = (vd.data.vendors || []).filter(
        (v) => v.applicant_type === "TECHNICIAN" && v.status === "APPROVED",
      );
      setTechnicians(list);
      setFixtureInfo(fixture.data.active ? fixture.data : null);
    } catch (e) {
      toast.error("Could not load bookings");
      setBookings([]);
    }
  };

  useEffect(() => { refreshAll(); }, []);

  const togglePreview = async (pilot) => {
    try {
      await api.post(`/admin/pilot/bookings/pilot/${pilot.pilot_id}/preview-toggle`, { enabled: !pilot.preview_bookings_enabled });
      toast.success(`preview_bookings_enabled → ${!pilot.preview_bookings_enabled}`);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const openBooking = async (id) => {
    try {
      const { data } = await api.get(`/admin/pilot/bookings/${id}`);
      setCurrent(data.booking);
      setEvents(data.events || []);
    } catch { toast.error("Could not load booking"); }
  };

  const doAssign = async () => {
    if (!chosen) return toast.error("Pick a technician");
    try {
      await api.post(`/admin/pilot/bookings/${assigning.booking_id}/assign`, { technician_id: chosen });
      toast.success("Assigned");
      setAssigning(null); setChosen("");
      await refreshAll();
      if (current?.booking_id === assigning.booking_id) openBooking(assigning.booking_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  const resolve = async (outcome) => {
    const reason = window.prompt(`Reason for ${outcome.toLowerCase()}?`);
    if (reason === null) return;
    try {
      await api.post(`/admin/pilot/bookings/${current.booking_id}/resolve`, { outcome, reason });
      toast.success(`Marked ${outcome}`);
      await refreshAll();
      openBooking(current.booking_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
  };

  const seedFixtures = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/pilot/bookings/fixtures/seed");
      setFixtureInfo(data);
      toast.success("Preview fixtures are ready");
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not seed preview fixtures");
    } finally {
      setSeeding(false);
    }
  };

  const clearFixtures = async () => {
    try {
      await api.post("/admin/pilot/bookings/fixtures/clear");
      setFixtureInfo(null);
      setCurrent(null);
      toast.success("Preview fixtures removed and pilot state restored");
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear preview fixtures");
    }
  };

  const resolveDispute = async () => {
    if (!current) return;
    if (!resolution.ca_opinion_reference.trim() || !resolution.reason.trim()) {
      toast.error("Add the CA opinion reference and resolution reason");
      return;
    }
    try {
      await api.post(`/admin/pilot/bookings/disputes/${current.booking_id}/resolve`, resolution);
      toast.success("Dispute resolution recorded without financial action");
      setResolvingDispute(false);
      setResolution({ outcome: "CANCELLED", ca_opinion_reference: "", reason: "" });
      await refreshAll();
      await openBooking(current.booking_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve dispute");
    }
  };

  if (bookings === null) return <Loading />;
  const filtered = filter ? bookings.filter((b) => b.status === filter) : bookings;
  const matchedTechs = current ? technicians.filter((t) => t.pilot_id === current.pilot_id) : technicians;

  return (
    <div className="p-6 space-y-6" data-testid="admin-pilot-bookings">
      <PageHeader
        title="Resident Technician Bookings"
        description="Super Admin dispatch for the Boisar pilot. Preview flow. No real payment or payout."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="seed-preview-fixtures-button"
          disabled={seeding}
          onClick={seedFixtures}
        >
          {seeding ? "Seeding preview fixtures…" : "Seed preview fixtures"}
        </Button>
        {fixtureInfo && (
          <Button
            data-testid="clear-preview-fixtures-button"
            onClick={clearFixtures}
            variant="outline"
          >
            Clear preview fixtures
          </Button>
        )}
        <span className="text-xs text-slate-600" data-testid="preview-fixture-safety-note">
          Fixtures are labelled, removable, and do not activate legal terms or payments.
        </span>
      </div>

      {fixtureInfo && (
        <Card data-testid="preview-fixture-credentials">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Browser demo access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-slate-600">{fixtureInfo.note}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {(fixtureInfo.logins || []).map((login) => (
                <div
                  className="border border-slate-200 bg-slate-50 p-3"
                  data-testid={`preview-fixture-login-${login.persona.toLowerCase()}`}
                  key={login.persona}
                >
                  <div className="font-medium">{login.persona}</div>
                  <div className="mt-1 break-all font-mono text-xs">{login.email}</div>
                  <div className="font-mono text-xs">{login.password}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {pilots.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> Pilots</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {pilots.map((p) => (
              <div key={p.pilot_id} className="flex flex-wrap items-center justify-between gap-3 border rounded-md p-2">
                <div className="min-w-0">
                  <div className="font-medium">{p.name} <span className="text-xs text-slate-500">· {p.status}</span></div>
                  <div className="text-xs text-slate-600">Cap: {p.cap?.approved || 0}/{p.cap?.max || 0} · allow_bookings=<b>{String(p.allow_bookings)}</b> · preview_bookings_enabled=<b>{String(p.preview_bookings_enabled || false)}</b></div>
                </div>
                <Button className="shrink-0" variant="outline" size="sm" onClick={() => togglePreview(p)} data-testid={`pilot-preview-toggle-${p.pilot_id}`}>
                  {p.preview_bookings_enabled ? (<><PowerOff className="h-4 w-4 mr-1" />Disable preview bookings</>) : (<><Power className="h-4 w-4 mr-1" />Enable preview bookings</>)}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">Bookings
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 h-8" data-testid="admin-pbk-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardTitle></CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500">No bookings{filter ? ` in state ${filter}` : ""}.</div>
          ) : (
            <table className="w-full text-sm border">
              <thead className="bg-slate-50"><tr>
                <th className="p-2 text-left">Created</th>
                <th className="p-2 text-left">Category</th>
                <th className="p-2 text-left">Resident</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Technician</th>
                <th className="p-2"></th>
              </tr></thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.booking_id} className="border-t">
                    <td className="p-2 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="p-2 text-xs">{b.category}</td>
                    <td className="p-2 text-xs">{b.resident_display || b.resident_email}</td>
                    <td className="p-2"><Status s={b.status} /></td>
                    <td className="p-2 text-xs">{b.technician_display || "—"}</td>
                    <td className="p-2 text-right flex gap-1 justify-end">
                      {["REQUESTED", "DECLINED_BY_TECH"].includes(b.status) && (
                        <Button size="sm" onClick={() => setAssigning(b)} data-testid={`admin-pbk-assign-${b.booking_id}`}>Assign</Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openBooking(b.booking_id)} data-testid={`admin-pbk-open-${b.booking_id}`}>Open</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Assign */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign technician</DialogTitle>
            <DialogDescription>
              Assign an approved technician from the matching pilot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-slate-600">Booking: <code>{assigning?.booking_id}</code></div>
            <div className="text-slate-600">Category: {assigning?.category}</div>
            <Select value={chosen} onValueChange={setChosen}>
              <SelectTrigger data-testid="admin-pbk-tech-select"><SelectValue placeholder="Pick an approved technician" /></SelectTrigger>
              <SelectContent>
                {technicians.filter((t) => !assigning || t.pilot_id === assigning.pilot_id).map((t) => (
                  <SelectItem key={t.vendor_id} value={t.vendor_id}>{t.business_name || t.vendor_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {technicians.filter((t) => !assigning || t.pilot_id === assigning.pilot_id).length === 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex gap-2 items-start"><AlertTriangle className="h-4 w-4 mt-0.5" /><span>No approved technicians attached to this pilot yet. Approve one in Vendor Management first.</span></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigning(null)}>Cancel</Button>
            <Button onClick={doAssign} data-testid="admin-pbk-assign-save">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!current} onOpenChange={(o) => !o && setCurrent(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Booking · {current?.category}</DialogTitle>
            <DialogDescription>
              Review booking details, activity, and preview-only actions.
            </DialogDescription>
          </DialogHeader>
          {current && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2 items-center"><Status s={current.status} /><span className="text-xs text-slate-500">{current.booking_id}</span></div>
              <div className="rounded-md border p-3 space-y-1">
                <div><b>Resident:</b> {current.resident_display} · {current.resident_phone}</div>
                <div><b>Address:</b> {current.resident_address}</div>
                <div><b>Description:</b> {current.description}</div>
                <div><b>Preferred slot:</b> {current.preferred_slot}</div>
                <div><b>Technician:</b> {current.technician_display || "—"}</div>
              </div>
              {current.estimate && (
                <div className="rounded-md border p-3 space-y-1">
                  <div className="font-medium">Estimate</div>
                  <div>Net visiting ₹{current.estimate.net_visiting_charged} + Labour ₹{current.estimate.labour} + Materials ₹{current.estimate.materials_total} + Taxes ₹{current.estimate.taxes} = <b>₹{current.estimate.grand_total}</b></div>
                  <div className="text-xs text-slate-600">Commission base: ₹{current.estimate.commission.commission_base} · {current.estimate.commission.commission_percent}% = ₹{current.estimate.commission.commission_amount}. Technician share ₹{current.estimate.commission.technician_share}.</div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {!["COMPLETED", "CANCELLED", "DISPUTED"].includes(current.status) && (
                  <>
                    <Button variant="destructive" onClick={() => resolve("CANCELLED")} data-testid="admin-pbk-cancel">Cancel booking</Button>
                    <Button variant="outline" onClick={() => resolve("COMPLETED")} data-testid="admin-pbk-force-complete">Force complete</Button>
                  </>
                )}
                {["REQUESTED", "DECLINED_BY_TECH"].includes(current.status) && (
                  <Button onClick={() => { setAssigning(current); setChosen(""); }} data-testid="admin-pbk-assign-from-detail">Assign technician</Button>
                )}
                {current.status === "DISPUTED" && (
                  <Button
                    data-testid="admin-pbk-resolve-dispute"
                    onClick={() => setResolvingDispute(true)}
                  >
                    Resolve dispute
                  </Button>
                )}
              </div>
              <div className="rounded-md border p-3">
                <div className="font-medium flex items-center gap-2 mb-1"><Clock className="h-4 w-4" /> Activity</div>
                {events.map((e) => (
                  <div key={e.event_id} className="text-xs text-slate-600"><span className="font-mono">{new Date(e.ts).toLocaleString()}</span> · <b>{e.event}</b> · {e.actor_role}{e.data?.reason ? ` · ${e.data.reason}` : ""}</div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={resolvingDispute}
        onOpenChange={(open) => !open && setResolvingDispute(false)}
      >
        <DialogContent className="max-w-lg" data-testid="admin-dispute-resolution-dialog">
          <DialogHeader>
            <DialogTitle>Resolve disputed booking</DialogTitle>
            <DialogDescription>
              Record the post-CA-opinion outcome. This preview action never creates a refund,
              payment, payout, or ledger movement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="mb-1 block font-medium" htmlFor="dispute-resolution-outcome">
                Outcome
              </label>
              <Select
                onValueChange={(outcome) => setResolution({ ...resolution, outcome })}
                value={resolution.outcome}
              >
                <SelectTrigger data-testid="admin-dispute-resolution-outcome">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CANCELLED">Cancel booking</SelectItem>
                  <SelectItem value="COMPLETED">Mark completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block font-medium" htmlFor="dispute-ca-opinion-reference">
                CA opinion reference
              </label>
              <input
                className="h-10 w-full border border-slate-300 px-3 text-sm"
                data-testid="admin-dispute-ca-opinion-reference"
                id="dispute-ca-opinion-reference"
                onChange={(event) => setResolution({
                  ...resolution,
                  ca_opinion_reference: event.target.value,
                })}
                value={resolution.ca_opinion_reference}
              />
            </div>
            <div>
              <label className="mb-1 block font-medium" htmlFor="dispute-resolution-reason">
                Resolution reason
              </label>
              <textarea
                className="min-h-24 w-full border border-slate-300 px-3 py-2 text-sm"
                data-testid="admin-dispute-resolution-reason"
                id="dispute-resolution-reason"
                onChange={(event) => setResolution({ ...resolution, reason: event.target.value })}
                value={resolution.reason}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="admin-dispute-resolution-cancel"
              onClick={() => setResolvingDispute(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button data-testid="admin-dispute-resolution-save" onClick={resolveDispute}>
              Record resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
